import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import StatusIcons from "../../../../../../../Components/StatusIcons/StatusIcons";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import FulfilmentCostBreakdown from "./FulfilmentCostBreakdown";
import { calculateTotalCost } from "./fulfilmentCost";
import { formatMoney } from "./money";

const Separator = () => (
  <span aria-hidden="true" className="shrink-0 px-[4px]">
    ·
  </span>
);

/**
 * Fulfilment column: the fulfilment status dropdown (same rules and handler
 * as before); underneath, the location, and for locations that aren't the
 * account's own (default) ones, the fulfilment fee (hover = breakdown,
 * click = cost details) and the billing status (click = billing pop-up).
 * Orders at a default location pay no fee and need no billing status.
 * Warehouse Orders passes showLocation={false} (its own location) and no
 * onOpenBilling, so the billing status is shown without a pop-up. The route
 * sheet passes showCharges={false} as well: the dropdown only ("No location"
 * under it when the order has none).
 */
const FulfilmentCell = ({
  row,
  orderStatus,
  canEdit,
  isDefaultLocation,
  onStatusChange,
  onOpenCost,
  onOpenBilling,
  showLocation = true,
  showCharges = true,
}) => {
  const selectedStatus = orderStatus.find(
    (status) => status.id === parseFloat(row?.fullfilment_status?.id),
  )?.id;

  const tooltipText = !canEdit
    ? "Not Allowed"
    : row?.type === "Return"
      ? "This Order is a Return"
      : row?.status !== "Confirmed"
        ? "This Order Is not Confirmed Yet!"
        : "";

  const isDisabled =
    !canEdit ||
    // 6 = cancelled; String() keeps the old loose == "6" for number or text ids.
    String(selectedStatus) === "6" ||
    row?.status !== "Confirmed" ||
    row?.type === "Return";

  const location = row?.warehouse?.name;
  const billing = row?.fulfillment_billing_status;

  return (
    <div className="flex w-full min-w-0 flex-col gap-[2px]">
      <div className="flex min-w-0">
        <Tooltip title={tooltipText}>
          <Select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e, row)}
            size="small"
            variant="standard"
            disableUnderline
            disabled={isDisabled}
            renderValue={(selected) => {
              const status = orderStatus.find((item) => item.id === selected);
              // "Attention / Partially Fulfilled" is shortened on the chip
              // (same colour); the menu and the hover keep the full name.
              const short = /^attention/i.test(status?.name ?? "")
                ? "Attention"
                : status?.name;

              return (
                <span title={status?.name} className="flex min-w-0">
                  <StatusIcons status={short} />
                </span>
              );
            }}
            sx={{
              // Sized to the chip, so the arrow sits right next to it.
              maxWidth: "100%",
              // MUI's own 1rem / 1.4375em would make the chip taller than
              // the table's other chips.
              fontSize: "inherit",
              lineHeight: "inherit",

              "&:hover": {
                backgroundColor: "var(--color-ui-surface-sunken)",
                borderRadius: "4px",
                boxShadow: "0 0 0 1px var(--color-ui-border) inset",
              },
              transition: "all 120ms ease",

              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                p: 0,
                minHeight: "unset !important",
                paddingRight: "20px !important",
              },

              "& .MuiSelect-icon": {
                fontSize: 16,
                right: 2,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    minHeight: "28px",
                    padding: "2px 8px",
                  },
                },
              },
            }}
          >
            {orderStatus.map((status) => (
              <MenuItem
                key={status.id}
                value={status.id}
                // A status the role can't pick (withStatusChoices).
                disabled={Boolean(status?.disabled)}
              >
                <StatusIcons status={status?.name} showTitle noBg noBorder />
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
      </div>

      <div className="flex min-w-0 items-center overflow-hidden font-normal text-ui-text-subtle">
        {!row?.warehouse_id ? (
          <span>No location</span>
        ) : (
          <>
            {showLocation && (
              <span title={location} className="min-w-0 truncate">
                {location ?? "-"}
              </span>
            )}
            {showCharges && !isDefaultLocation && (
              <>
                {showLocation && <Separator />}
                <Tooltip title={<FulfilmentCostBreakdown row={row} />}>
                  <button
                    type="button"
                    onClick={() => onOpenCost(row)}
                    className="shrink-0 border-0 bg-transparent p-0 text-inherit hover:text-brand"
                  >
                    {formatMoney(calculateTotalCost(row))}
                  </button>
                </Tooltip>
                {billing && (
                  <>
                    <Separator />
                    {/* The badge's 3px vertical padding is taken back; the
                        line's overflow-hidden clips it so the cell can't
                        scroll. */}
                    {onOpenBilling ? (
                      <button
                        type="button"
                        title="Billing status"
                        onClick={() => onOpenBilling(row)}
                        className="-my-[3px] flex shrink-0 border-0 bg-transparent p-0"
                      >
                        <StatusIcons
                          status={billing}
                          noBg={true}
                          noDot={true}
                        />
                      </button>
                    ) : (
                      <span
                        title="Billing status"
                        className="-my-[3px] flex shrink-0"
                      >
                        <StatusIcons
                          status={billing}
                          noBg={true}
                          noDot={true}
                        />
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FulfilmentCell;
